import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Stack,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Star as StarIcon
} from '@mui/icons-material';

const SummariesTabV2 = ({ summaries = [], onUpdate, onDirtyChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localSummaries, setLocalSummaries] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    setLocalSummaries([...summaries]);
  }, [summaries]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  const handleEdit = () => setIsEditing(true);

  const handleSave = () => {
    onUpdate(localSummaries);
    setIsEditing(false);
    setIsDirty(false);
  };

  const handleCancel = () => {
    setLocalSummaries([...summaries]);
    setIsEditing(false);
    setIsDirty(false);
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleAddNew = () => {
    setEditForm({
      title: '',
      text: '',
      is_default: false,
      tags: []
    });
    setEditingIndex(localSummaries.length);
  };

  const handleEditItem = (index) => {
    setEditForm({ ...localSummaries[index] });
    setEditingIndex(index);
  };

  const handleSaveItem = () => {
    const updated = [...localSummaries];
    if (editingIndex < updated.length) {
      updated[editingIndex] = editForm;
    } else {
      updated.push(editForm);
    }
    setLocalSummaries(updated);
    setEditingIndex(null);
    setEditForm(null);
    setIsDirty(true);
  };

  const handleCancelItem = () => {
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleDeleteItem = (index) => {
    if (window.confirm('Delete this summary?')) {
      const updated = localSummaries.filter((_, i) => i !== index);
      setLocalSummaries(updated);
      setIsDirty(true);
    }
  };

  const renderItemForm = () => (
    <Card sx={{ mb: 2, bgcolor: 'action.hover' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {editingIndex < localSummaries.length ? 'Edit Summary' : 'Add Summary'}
        </Typography>

        <TextField
          fullWidth
          label="Title"
          value={editForm.title}
          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
          placeholder="e.g., Technical Summary, Executive Summary"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          multiline
          rows={6}
          label="Summary Text"
          value={editForm.text}
          onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
          placeholder="Write your professional summary..."
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Tags (comma-separated)"
          value={editForm.tags?.join(', ') || ''}
          onChange={(e) => setEditForm({
            ...editForm,
            tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
          })}
          placeholder="e.g., technical, leadership, ai"
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={editForm.is_default}
              onChange={(e) => setEditForm({ ...editForm, is_default: e.target.checked })}
            />
          }
          label="Set as default summary"
        />

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleCancelItem} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveItem} variant="contained" startIcon={<SaveIcon />}>
            Save Item
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderViewItem = (summary, index) => (
    <Card key={index} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">{summary.title}</Typography>
              {summary.is_default && (
                <Chip icon={<StarIcon />} label="Default" size="small" color="primary" />
              )}
            </Box>
            <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
              {summary.text}
            </Typography>
            {summary.tags && summary.tags.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                {summary.tags.map((tag, i) => (
                  <Chip key={i} label={tag} size="small" variant="outlined" />
                ))}
              </Stack>
            )}
          </Box>
          {isEditing && (
            <Stack direction="row" spacing={1}>
              <IconButton onClick={() => handleEditItem(index)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDeleteItem(index)} size="small" color="error">
                <DeleteIcon />
              </IconButton>
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Professional Summaries</Typography>
        {!isEditing ? (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
            Edit
          </Button>
        ) : (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!isDirty}>
              Save Changes
            </Button>
          </Stack>
        )}
      </Box>

      {isDirty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Click "Save Changes" to commit them.
        </Alert>
      )}

      {isEditing && editingIndex === null && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew} sx={{ mb: 2 }}>
          Add Summary
        </Button>
      )}

      {editingIndex !== null && editForm && renderItemForm()}

      {localSummaries.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No summaries added yet.
          {isEditing && ' Click "Add Summary" to get started.'}
        </Typography>
      ) : (
        localSummaries.map((summary, index) =>
          editingIndex === index ? null : renderViewItem(summary, index)
        )
      )}
    </Box>
  );
};

export default SummariesTabV2;
